import React, {Component} from 'react';
import axios from 'axios';
import uniqueId from 'lodash.uniqueid';

import {kNIntoTonsRound} from './utils';
import ControlPanel from './ControlPanel/ControlPanel';
import GroupedRopesOrderingTable
  from './GroupedRopesOrderingTable/GroupedRopesOrderingTable';
import PlainRopesOrderingTable
  from './PlainRopesOrderingTable/PlainRopesOrderingTable';
import PlainStretchersOrderingTable
    from './PlainStretchersOrderingTable/PlainStretchersOrderingTable';
import BundleOrderingTable
    from './BundleOrderingTable/BundleOrderingTable';
import BundlePackageOrderingTable
    from './BundlePackageOrderingTable/BundlePackageOrderingTable';

const tableTypes = Object.freeze ({
  RopesGrouped: 'RopesGrouped',
  RopesNotGrouped: 'RopesNotGrouped',
  RopeStretchers: 'RopeStretchers',
  Bundle: 'Bundle',
  BundlePackageGroup: 'BundlePackageGroup',
  None: 'None',
});

const validateSdmbl = sdmbl => {
  return sdmbl.visible === 'TRUE' && parseFloat(sdmbl.min) >= 1  && parseFloat(sdmbl.max) > parseFloat(sdmbl.min);
};

const showTDBF = sdmbl => {
  return sdmbl.visible === 'TRUE' && parseFloat(sdmbl.min) > 1  && parseFloat(sdmbl.max) > parseFloat(sdmbl.min);
}

export default class OrderingInfoWithVariants extends Component {
  constructor (props) {
    super (props);

    this.state = {
      orderingItems: [],
      minRangeValueInkN: 0,
      maxRangeValueInkN: 0,
      rangeType: 'spliced',
      tableType: tableTypes.None,
      isLoaded: false,
    };

    this.minMaxSpliced = {min: 0, max: 0};
    this.minMaxUnspliced = {min: 0, max: 0};
    this.minMax = {min: 0, max: 0};

    this.fetchData = this.fetchData.bind (this);
    this.transformResults = this.transformResults.bind (this);
    this.handleRangeChange = this.handleRangeChange.bind (this);
    this.handleSpliceChange = this.handleSpliceChange.bind (this);
    this.filterOrderingItems = this.filterOrderingItems.bind (this);
    this.findMinMax = this.findMinMax.bind (this);
    this.findPackagesMinMax = this.findPackagesMinMax.bind (this);
    this.validateValue = this.validateValue.bind (this);
    this.getTableType = this.getTableType.bind (this);
  }

  componentDidMount () {
    this.fetchData (this.props.productId);
  }

  findMinMax (data, property) {
    return {
      min: data.reduce (
        (min, item) => (item[property] < min ? item[property] : min),
        data[0][property]
      ),
      max: data.reduce (
        (max, item) => (item[property] > max ? item[property] : max),
        data[0][property]
      ),
    };
  }

  findPackagesMinMax (data) {
    let min = 0;
    let max = 0;

    data.forEach(packageData => {
      const itemToCheck = packageData.Items[0];
      const itemMin = Number(itemToCheck.From);
      const itemMax = Number(itemToCheck.To);

      if (itemMin < min || min === 0) min = itemMin;
      if (itemMax > max) max = itemMax;
    })

    return { min, max };
  }
 
  handleRangeChange (min, max) {
    if (this.state.rangeType === 'sdmbl') {
      const maxValue = Math.min(Math.round(min * Number(this.props.sdmbl.max)), this.minMax.max);
      this.setState({
        minRangeValueInkN: min,
        maxRangeValueInkN: maxValue
      });
    } else {
      this.setState({
        minRangeValueInkN: min,
        maxRangeValueInkN: max
      });
    }
  }

  validateValue (min, max) {
    switch (true) {
      case min < this.minMax.min:
        min = this.minMax.min;
        this.setState ({...this.state, minRangeValueInkN: this.minMax.min});
        if (this.state.rangeType === 'sdmbl') {
          const newMax = Math.min(Math.round(this.minMax.min * Number(this.props.sdmbl.max)), this.minMax.max);
          max = newMax;
          this.setState ({...this.state, minRangeValueInkN: this.minMax.min, maxRangeValueInkN: newMax});
        }
        break;
      case min > this.minMax.max:
        min = this.minMax.max;
        this.setState ({...this.state, minRangeValueInkN: this.minMax.max});
        break;
      case max > this.minMax.max:
        max = this.minMax.max;
        this.setState ({...this.state, maxRangeValueInkN: this.minMax.max});
        break;
      case max < this.minMax.min:
        max = this.minMax.min;
        this.setState ({...this.state, maxRangeValueInkN: this.minMax.min});
        break;
      default:
      // Everything is correct - don't change the state
    }

    if (this.state.rangeType === 'sdmbl' && min >= this.minMax.min) {
      const newMax = Math.min(Math.round(min * Number(this.props.sdmbl.max)), this.minMax.max);
      if (newMax !== max) {
        max = newMax;
        this.setState ({...this.state, maxRangeValueInkN: newMax});
      }
    }

    // Swap min and max values if needed
    if (min > max) {
      let temp = min;
      min = max;
      max = temp;
      this.setState ({minRangeValueInkN: min, maxRangeValueInkN: max});
    }
  }

  handleSpliceChange (value) {
    const { sdmbl } = this.props;
    let { minRangeValueInkN, maxRangeValueInkN } = this.state;

    const originalEdges = this.minMax;
    let newTargetEdges = originalEdges;

    if (value === 'sdmbl' && showTDBF(sdmbl)) {
        newTargetEdges = {
            min: originalEdges.min / parseFloat(sdmbl.min),
            max: originalEdges.max / parseFloat(sdmbl.max),
        };
    }

    // If current min value is out of the new bounds, reset the slider
    if (minRangeValueInkN < newTargetEdges.min) {
        minRangeValueInkN = newTargetEdges.min;
        maxRangeValueInkN = originalEdges.max; // Reset max to the absolute max
    }

    // If the new tab is 'sdmbl', max value depends on min value
    if (value === 'sdmbl') {
        maxRangeValueInkN = Math.min(Math.round(minRangeValueInkN * Number(sdmbl.max)), originalEdges.max);
    }

    this.setState({
        rangeType: value,
        minRangeValueInkN,
        maxRangeValueInkN,
    });
  }

  filterOrderingItems () {
    const sdmblMinValue = parseFloat(this.props.sdmbl.min);
    const sdmblThumbValue = Math.min(this.state.minRangeValueInkN * sdmblMinValue, this.minMax.max);
    const showSdmblThumb = sdmblMinValue !== 1 && this.state.rangeType === 'sdmbl';

    if (this.state.tableType === tableTypes.RopesGrouped) {
      return this.state.orderingItems.filter (item => {
        const minBreakLoad = this.state.rangeType === 'spliced' || this.state.rangeType === 'sdmbl'
          ? item.MBLSplicedInkN
          : item.MBLUnsplicedInkN;
        return (
          minBreakLoad >= (showSdmblThumb ? sdmblThumbValue : this.state.minRangeValueInkN) &&
          minBreakLoad <= this.state.maxRangeValueInkN
        );
      });
    } else if(this.state.tableType === tableTypes.BundlePackageGroup) {
        return this.state.orderingItems.filter(packageData => {
          const itemToFilter = packageData.Items[0];

          const itemMinRangeValueInkN = Number(itemToFilter.From);
          const itemMaxRangeValueInkN = Number(itemToFilter.To);

          const filterMinRangeValueInkN = (showSdmblThumb ? sdmblThumbValue : this.state.minRangeValueInkN);
          const filterMaxRangeValueInkN = this.state.maxRangeValueInkN;

          const isInRange =
              (filterMinRangeValueInkN <= itemMinRangeValueInkN && itemMinRangeValueInkN <= filterMaxRangeValueInkN) ||   // Case 1: range2 starts within range1
              (filterMinRangeValueInkN <= itemMaxRangeValueInkN && itemMaxRangeValueInkN <= filterMaxRangeValueInkN) ||   // Case 2: range2 ends within range1
              (itemMinRangeValueInkN <= filterMinRangeValueInkN && filterMinRangeValueInkN <= itemMaxRangeValueInkN) ||   // Case 3: range1 starts within range2
              (itemMinRangeValueInkN <= filterMaxRangeValueInkN && filterMaxRangeValueInkN <= itemMaxRangeValueInkN);    // Case 4: range1 ends within range2
          return isInRange;
      });
    } else {
      return this.state.orderingItems.filter (item => {
        const minLDBF = item.LDBFMinInkN;
        const maxLDBF = item.LDBFMaxInkN;
        const minVale = this.state.rangeType === 'spliced' || this.state.rangeType === 'sdmbl'
          ? (showSdmblThumb ? sdmblThumbValue : this.state.minRangeValueInkN)
          : this.state.minRangeValueInkN / 1.1;
        const maxVale = this.state.rangeType === 'spliced' || this.state.rangeType === 'sdmbl'
          ? this.state.maxRangeValueInkN
          : this.state.maxRangeValueInkN / 1.1;
        return minLDBF <= maxVale && maxLDBF >= minVale;
      });
    }
  }

  getTableType (data) {
    if (data.CalculatorType) {
      return data.CalculatorType;
    } else if (data.Packages) {
      return tableTypes.BundlePackageGroup
    } else {
      return tableTypes.Bundle;
    }
  };

  fetchData (id) {
    return axios
      .get (this.props.apiRootUrl, {
        params: {
          id: id,
        },
      })
      .then (response => {
        const type = this.getTableType(response.data);
        const data = response.data.CalculatorType ? this.transformResults (response.data.Lines) : response.data.Packages;

        if (type === tableTypes.RopesGrouped) {
          this.minMaxSpliced = this.findMinMax (data, 'MBLSplicedInkN');
          this.minMaxUnspliced = this.findMinMax (data, 'MBLUnsplicedInkN');

          // Set global min-max values
          this.minMax.min = this.minMaxSpliced.min < this.minMaxUnspliced.min
            ? this.minMaxSpliced.min
            : this.minMaxUnspliced.min;
          this.minMax.max = this.minMaxSpliced.max > this.minMaxUnspliced.max
            ? this.minMaxSpliced.max
            : this.minMaxUnspliced.max;
        } else if (type === tableTypes.BundlePackageGroup) {
          const minMax = this.findPackagesMinMax (data);
          this.minMax.min = minMax.min;
          this.minMax.max = minMax.max;
        } else {
          this.minMax.min = this.findMinMax (data, 'LDBFMinInkN').min;
          this.minMax.max = this.findMinMax (data, 'LDBFMaxInkN').max;
        }

        const initialMin = this.minMax.min;
        const initialMax = this.minMax.max;

        const rangeValues = [initialMin, initialMax].sort((a, b) => a - b);

        this.setState ({
            isLoaded: true,
            orderingItems: data,
            minRangeValueInkN: rangeValues[0],
            maxRangeValueInkN: rangeValues[1],
            tableType: type,
        });
    })
      .catch (function (error) {
        if (error.response) {
          console.log (error.response.data);
          console.log (error.response.status);
          console.log (error.response.headers);
        } else if (error.request) {
          console.log (error.request);
        } else {
          console.log ('Error', error.message);
        }
        console.log (error.config);
      });
  }

  transformResults (data) {
    return data.map (item => ({
      Id: uniqueId (),
      Diameter: Number (item.Diameter),
      MBLSplicedInkN: Number (item.MBLSplicedInkN),
      MBLUnsplicedInkN: Number (item.MBLUnsplicedInkN),
      MBLSplicedInTons: kNIntoTonsRound (Number (item.MBLSplicedInkN)),
      MBLUnsplicedInTons: kNIntoTonsRound (Number (item.MBLUnsplicedInkN)),
      LDBFMaxInTons: kNIntoTonsRound (Number (item.LDBFMaxInkN)),
      LDBFMaxInkN: Number (item.LDBFMaxInkN),
      LDBFMinInTons: kNIntoTonsRound (Number (item.LDBFMinInkN)),
      LDBFMinInkN: Number (item.LDBFMinInkN),
      Products: item.Products,
      Description: item.Name,
      Number: item.Number,
      Length: item.Length,
    }));
  }

  displayControlPanel () {
    return (
      this.state.tableType == tableTypes.RopesGrouped ||
      this.state.tableType == tableTypes.RopesNotGrouped ||
      this.state.tableType == tableTypes.BundlePackageGroup
    );
  }
  renderTable () {
    switch (this.state.tableType) {
      case tableTypes.RopesGrouped:
        return (
          <GroupedRopesOrderingTable
            orderingItems={this.filterOrderingItems ()}
          />
        );
      case tableTypes.RopesNotGrouped:
        return (
          <PlainRopesOrderingTable
            orderingItems={this.filterOrderingItems ()}
            tableType={this.state.tableType}
            showTDBF={showTDBF(this.props.sdmbl)}
          />
        );
      case tableTypes.RopeStretchers:
        return (
          <PlainStretchersOrderingTable
            orderingItems={this.state.orderingItems}
          />
        );
      case tableTypes.Bundle:
        return (
          <BundleOrderingTable
            orderingItems={this.state.orderingItems}
          />
        );
      case tableTypes.BundlePackageGroup:
        return (
          <BundlePackageOrderingTable
            orderingItems={this.filterOrderingItems ()}
          />
        );
      default:
        return null;
    }
  }

  render () {
    return (
      <React.Fragment>
        {this.state.isLoaded &&
          <div>
            {this.displayControlPanel () &&
              <ControlPanel
                handleRangeChange={this.handleRangeChange}
                handleSpliceChange={this.handleSpliceChange}
                validateValue={this.validateValue}
                minRangeValueInkN={this.state.minRangeValueInkN}
                maxRangeValueInkN={this.state.maxRangeValueInkN}
                rangeType={this.state.rangeType}
                edgeValues={this.minMax}
                isMBL={this.state.tableType == tableTypes.RopesGrouped}
                showStrengthTypes={this.state.tableType !== tableTypes.BundlePackageGroup}
                sdmbl={this.props.sdmbl}
                showSdmbl={validateSdmbl(this.props.sdmbl)}
              />}
            {this.renderTable ()}
          </div>}
      </React.Fragment>
    );
  }
}
